import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import { useMatches } from 'react-router-dom'
import { cn } from '../lib/cn'

/**
 * THE PAGE CONTRACT: header, then tab strip, then content. Every page.
 *
 * The design sheet draws the right pane as three bands in that order, and the
 * reason to write it as one component rather than as guidance is that guidance
 * was what we had. Eleven screens hand-rolled a title at a size the shared one
 * does not use, in two different alignments, and the inbox shipped with no
 * title at all: the URL changed and nothing on screen said where you were.
 *
 * So the decisions below are taken once, here, and a page that wants a heading
 * gets them whether or not anybody reads this comment.
 */

/**
 * ONE HAIRLINE CLOSES THE BAND, AND THE TAB STRIP OWNS IT WHEN THERE IS ONE.
 *
 * Measured off the reference rather than guessed: a page with tabs draws no
 * line between the title and the tabs, and the line under the tabs is the edge
 * of the whole band. A page without tabs draws that same line under the title.
 * Either way a reader sees one rule between the heading and the content, which
 * is what makes a tabbed page and a plain one feel like the same app.
 *
 * `Tabs` and `TabNav` both carry `border-b border-line`, so the slot below adds
 * no line of its own. Anything else dropped into that slot has to bring one.
 */
const BAND_EDGE = 'border-b border-line'

/**
 * `page` is the band at the top of a route. `section` is the same header
 * drawn as the title inside a pane that already has a page header above it,
 * which is what a settings screen is inside the settings shell: 18/28 600 in
 * the full ink, no 68px row and no band rule, because the shell's own strip
 * already drew the edge.
 */
export type PageHeaderVariant = 'page' | 'section'

/**
 * The variant a header takes when it is not given one.
 *
 * A shell that renders whole screens in its pane wraps the pane in
 * `SectionHeaders`, and every screen header inside it becomes a section title
 * without the screen knowing where it is mounted. That replaced descendant
 * selectors on the pane, which reached any `header` and `h1` underneath and
 * could not be covered except by reading class strings.
 */
const VariantContext = createContext<PageHeaderVariant>('page')

/** Draw every `PageHeader` inside as a section title. */
export function SectionHeaders({ children }: { children: ReactNode }) {
  return <VariantContext.Provider value="section">{children}</VariantContext.Provider>
}

export interface PageHeaderProps {
  title: ReactNode
  /** One line under the title. A band is a heading, not an introduction. */
  description?: ReactNode
  /** Where this page sits, above the title. A link back, usually. */
  breadcrumb?: ReactNode
  /**
   * Right-aligned controls, and AT MOST ONE OF THEM IS `variant="primary"`.
   *
   * That rule is the whole reason the reference never looks busy, and it is
   * cheap to break by accident: two people each add the obvious button for
   * their own feature and neither sees the other. `PageHeader.test.tsx` counts
   * the primaries in every call site so the second one fails the suite rather
   * than shipping.
   */
  actions?: ReactNode
  /**
   * The tab strip for this page. `TabNav` for tabs that are really navigation,
   * which is most of them, so the tab a person is looking at has a URL and can
   * be sent to somebody.
   */
  tabs?: ReactNode
  /** A small hint beside the title, for the genuinely technical. */
  help?: ReactNode
  /** An optional 20px glyph before the title, drawn in the subtle ink. */
  icon?: ReactNode
  /**
   * `page` by default, or whatever the nearest `SectionHeaders` says. An
   * explicit value wins over both.
   */
  variant?: PageHeaderVariant
  className?: string
}

/**
 * The band at the top of every route.
 *
 * NOT STICKY, DELIBERATELY. The obvious next thought is to pin this while the
 * content scrolls, and two things say no. The reference does not pin it, and
 * `main` is the only scroll container in the app, so a sticky header here would
 * have to fight the shell for which element owns the scroll. `stacking.test.ts`
 * asserts that no component draws a header pinned to the top, and the test file
 * beside this one extends that scan to the pages, where the same mistake would
 * otherwise pass unnoticed.
 *
 * ONE TITLE SIZE AND ONE ALIGNMENT, and the size is the reference's. Salesforge
 * sets its page title at 14/20 semibold in the secondary ink, measured, which
 * looks small written down and reads as calm on screen, because the content is
 * the loudest thing in the pane and not its name. The row is 68px tall and
 * vertically centred, which is only possible because the description sits
 * UNDER the row rather than inside the title column: with the description in
 * the column, a header with one and a header without one centre their buttons
 * against different things.
 *
 * The actions sit 12px apart and the primary goes last. `PageHeader.test.tsx`
 * counts the primaries at every call site, which is the warning this component
 * cannot give for itself because the buttons arrive already built.
 */
export function PageHeader({
  title,
  description,
  breadcrumb,
  actions,
  tabs,
  help,
  icon,
  variant,
  className,
}: PageHeaderProps) {
  const inherited = useContext(VariantContext)
  const section = (variant ?? inherited) === 'section'
  // `|| undefined` so a tabbed header with nothing to say draws no `class=""`.
  return (
    <header
      data-variant={section ? 'section' : undefined}
      className={cn(!tabs && !section && BAND_EDGE, className) || undefined}
    >
      <div
        className={
          section
            ? 'flex flex-wrap items-center gap-x-4 gap-y-2 pb-3'
            : 'flex min-h-header flex-wrap items-center gap-x-4 gap-y-2 py-3'
        }
      >
        <div className="min-w-0">
          {breadcrumb ? <div className="mb-1 text-xs text-ink-subtle">{breadcrumb}</div> : null}
          <div className="flex min-w-0 items-center gap-2">
            {icon ? <span className="grid h-5 w-5 shrink-0 place-items-center text-ink-subtle">{icon}</span> : null}
            <h1
              className={cn(
                'truncate',
                section ? 'text-xl font-semibold text-ink' : 'text-base font-semibold text-ink-secondary',
              )}
            >
              {title}
            </h1>
            {help ? <span className="shrink-0 text-ink-faint">{help}</span> : null}
          </div>
        </div>
        {/*
         * `ml-auto` rather than `justify-between`, so the actions stay on the
         * right when a long title pushes them onto a line of their own. With
         * `justify-between` a wrapped line holds one item and lands left, which
         * reads as a mistake at exactly the width where it happens.
         */}
        {actions ? <div className="ml-auto flex shrink-0 flex-wrap items-center gap-3">{actions}</div> : null}
      </div>
      {description ? <p className="-mt-1 mb-3 max-w-2xl text-base text-ink-muted">{description}</p> : null}
      {/* The strip draws its own rule, so the band adds none when there is one. */}
      {tabs ? <div className="min-h-tabstrip">{tabs}</div> : null}
    </header>
  )
}

/** Only the part of a route match this reads, so nothing here imports the route table. */
export interface TitledMatch {
  handle?: unknown
}

/**
 * The title the route table already gives this screen.
 *
 * `routes.tsx` puts a `handle` with a title on every route and nothing has ever
 * read one, so a screen's name is written down in exactly the place a screen
 * cannot see it. Deepest match first, because the nested route is the screen
 * you are on and the parent is the section it lives in.
 */
export function routeTitle(matches: readonly TitledMatch[]): string | undefined {
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const handle = matches[index]?.handle
    if (!handle || typeof handle !== 'object') continue
    const title = (handle as { title?: unknown }).title
    if (typeof title === 'string' && title.trim() !== '') return title
  }
  return undefined
}

/**
 * `<PageHeader title={useRouteTitle() ?? 'Something'} />` for a page whose
 * title is already in the route table.
 *
 * It is a hook beside the component rather than a fallback inside it on
 * purpose. `useMatches` throws outside a data router, and a heading that cannot
 * render in a test or in isolation is a heading nobody will cover.
 */
export function useRouteTitle(): string | undefined {
  return routeTitle(useMatches())
}
